import { Form } from 'antd';
import type { FormInstance } from 'antd';
import type { CargoItemFormValue, CargoTableRow } from './sharedTransportFormRows';
import type { SharedTransportFormValues } from './sharedTransportFormUtils';

export const TRANSPORT_ORDER_SELECTION_ANCHOR_ID = 'transport-order-selection';

export function buildTransportAddressAnchorId(variant: 'loading' | 'delivery'): string {
  return `transport-${variant}Address`;
}

export function buildTransportGoodsValueAnchorId(cargoKey: string): string {
  const selectorSafeKey = Array.from(cargoKey, (character) =>
    /[a-zA-Z0-9-]/.test(character)
      ? character
      : `_${character.codePointAt(0)?.toString(16)}_`
  ).join('');
  return `transport-goods-value-${selectorSafeKey}`;
}

export type TransportRequiredFieldKind =
  | 'order'
  | 'loadingDate'
  | 'loadingAddress'
  | 'estimatedDelivery'
  | 'deliveryAddress'
  | 'transportCompany'
  | 'goodsValue';

export interface MissingTransportRequiredField {
  id: string;
  kind: TransportRequiredFieldKind;
  namePath?: Array<string | number>;
  productName?: string;
  orderNumber?: string;
}

interface TransportRequiredFieldValues {
  loadingDate?: unknown;
  loadingAddresses?: unknown;
  estimatedDelivery?: unknown;
  deliveryAddresses?: unknown;
  transportCompany?: unknown;
  cargoItemsByKey?: Record<string, CargoItemFormValue>;
}

interface GetMissingTransportRequiredFieldsInput {
  values: TransportRequiredFieldValues;
  selectedOrderIds: string[];
  cargoRows: CargoTableRow[];
  canEditTransportCosts: boolean;
}

function hasNonBlankText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasNonBlankAddress(addresses: unknown): boolean {
  return Array.isArray(addresses) && addresses.some(hasNonBlankText);
}

function hasGoodsValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  return Number.isFinite(Number(value));
}

/**
 * Single completion contract for the persistent transport-form checklist.
 *
 * Existing antd rules still own inline validation (including ranges and
 * formatting). This helper owns only the operator-facing answer to “what data
 * is still missing?” so the sticky checklist, its live updates, and tests
 * cannot drift into separate hand-written required-field lists.
 */
export function getMissingTransportRequiredFields({
  values,
  selectedOrderIds,
  cargoRows,
  canEditTransportCosts,
}: GetMissingTransportRequiredFieldsInput): MissingTransportRequiredField[] {
  const missing: MissingTransportRequiredField[] = [];

  if (selectedOrderIds.length === 0) {
    missing.push({ id: 'order', kind: 'order' });
  }
  if (!values.loadingDate) {
    missing.push({ id: 'loadingDate', kind: 'loadingDate', namePath: ['loadingDate'] });
  }
  if (!hasNonBlankAddress(values.loadingAddresses)) {
    missing.push({ id: 'loadingAddress', kind: 'loadingAddress', namePath: ['loadingAddresses'] });
  }
  if (!values.estimatedDelivery) {
    missing.push({
      id: 'estimatedDelivery',
      kind: 'estimatedDelivery',
      namePath: ['estimatedDelivery'],
    });
  }
  if (!hasNonBlankAddress(values.deliveryAddresses)) {
    missing.push({
      id: 'deliveryAddress',
      kind: 'deliveryAddress',
      namePath: ['deliveryAddresses'],
    });
  }
  if (!hasNonBlankText(values.transportCompany)) {
    missing.push({
      id: 'transportCompany',
      kind: 'transportCompany',
      namePath: ['transportCompany'],
    });
  }

  if (canEditTransportCosts) {
    const cargoItemsByKey = values.cargoItemsByKey || {};
    cargoRows
      .filter((row) => Number(row.quantity || 0) > 0)
      .forEach((row) => {
        if (!hasGoodsValue(cargoItemsByKey[row.key]?.goodsValueTotal)) {
          missing.push({
            id: `goodsValue:${row.key}`,
            kind: 'goodsValue',
            namePath: ['cargoItemsByKey', row.key, 'goodsValueTotal'],
            productName: row.productName,
            orderNumber: row.orderNumber,
          });
        }
      });
  }

  return missing;
}

interface UseTransportRequiredFieldsInput {
  form: FormInstance<SharedTransportFormValues>;
  selectedOrderIds: string[];
  cargoRows: CargoTableRow[];
  canEditTransportCosts: boolean;
}

/** Live bridge from antd Form state to the single completion contract above. */
export function useTransportRequiredFields({
  form,
  selectedOrderIds,
  cargoRows,
  canEditTransportCosts,
}: UseTransportRequiredFieldsInput): MissingTransportRequiredField[] {
  const loadingDate = Form.useWatch('loadingDate', form);
  const loadingAddresses = Form.useWatch('loadingAddresses', form);
  const estimatedDelivery = Form.useWatch('estimatedDelivery', form);
  const deliveryAddresses = Form.useWatch('deliveryAddresses', form);
  const transportCompany = Form.useWatch('transportCompany', form);
  const cargoItemsByKey = Form.useWatch('cargoItemsByKey', form) as
    | Record<string, CargoItemFormValue>
    | undefined;

  return getMissingTransportRequiredFields({
    values: {
      loadingDate,
      loadingAddresses,
      estimatedDelivery,
      deliveryAddresses,
      transportCompany,
      cargoItemsByKey,
    },
    selectedOrderIds,
    cargoRows,
    canEditTransportCosts,
  });
}

export function focusTransportRequiredField(
  form: FormInstance<SharedTransportFormValues>,
  field: MissingTransportRequiredField,
  root: Document = document,
): void {
  if (field.kind === 'order') {
    const orderSelection = root.getElementById(TRANSPORT_ORDER_SELECTION_ANCHOR_ID);
    orderSelection?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const rowCheckbox = orderSelection?.querySelector<HTMLInputElement>(
      '.ant-table-tbody input[type="checkbox"], tbody input[type="checkbox"]',
    );
    const fallbackCheckbox = orderSelection?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    (rowCheckbox || fallbackCheckbox)?.focus({ preventScroll: true });
    return;
  }

  if (field.kind === 'loadingAddress' || field.kind === 'deliveryAddress') {
    const variant = field.kind === 'loadingAddress' ? 'loading' : 'delivery';
    const addressSection = root.getElementById(buildTransportAddressAnchorId(variant));
    addressSection?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    addressSection?.querySelector<HTMLElement>('textarea')?.focus({ preventScroll: true });
    return;
  }

  if (field.kind === 'goodsValue') {
    const cargoKey = String(field.namePath?.[1] || '');
    const goodsValue = root.getElementById(buildTransportGoodsValueAnchorId(cargoKey));
    goodsValue?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    goodsValue?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    return;
  }

  if (field.namePath) {
    form.scrollToField(field.namePath, {
      behavior: 'smooth',
      block: 'center',
      focus: true,
    });
  }
}
